/* eslint-disable no-eval */
import { useCallback, useEffect, useMemo, useState } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import { formatNumber, formatPercentage } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import { getColorBasedOnNumber } from 'utils/general';
import { CorporateActionStockType } from 'constants/trading';
import {
  getBuyAmountInvested,
  getProfitLoss,
  getSellAmountInvestedFee,
  getSellGain,
} from 'global/Securities/utils/formula';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import { Skeleton } from 'antd';
import { PreviewItem } from './styled';
import orderbookModalStore from './store';

// TODO: Set a dumb component to be used by all preview order
// TODO: Discuss logic where to get company info, since v2 also have incorrect behavior

interface SellAllPreviewProps {
  onCancel?(): void;
}

function SellAllPreview({ onCancel = () => {} }: SellAllPreviewProps) {
  const [isCompanyLoading, setCompanyLoading] = useState(false);
  const {
    company,
    configFormula: {
      data: { formula },
    },
    getCompany,
  } = tradingDataStore();
  const { price, symbol } = orderbookModalStore();
  const portfolioSymbol = useGetPortfolioData(symbol, false, true);
  const productType = company?.data?.type_company || '';

  const isCorporateAction = useMemo(
    () => CorporateActionStockType.includes(productType.toLowerCase()),
    [productType],
  );

  const LOT = Math.floor(portfolioSymbol.available_lot);
  const PRICE = price;
  const sellAmountInvestedFee = getSellAmountInvestedFee(
    LOT,
    price,
    isCorporateAction,
  );
  const buyAmountInvested = getBuyAmountInvested(
    LOT,
    portfolioSymbol ? portfolioSymbol.price_average_fee : 0,
  );
  const sellProfitLoss = getProfitLoss(
    sellAmountInvestedFee,
    buyAmountInvested,
  );
  const sellGain = getSellGain(sellProfitLoss, buyAmountInvested);

  const FEE_SELL = formula.fee_sell;
  const SELL_FEE = eval(formula.sell_amount_fee); // nosemgrep
  const SELL_AMOUNT = eval(formula.sell_amount_invested); // nosemgrep

  const previewData = [
    {
      label: <Text>Stock</Text>,
      value: <Text family="bold">{symbol}</Text>,
    },
    {
      label: <Text>Lot</Text>,
      value: <Text>{formatNumber(LOT, false)}</Text>,
    },
    {
      label: <Text>Price</Text>,
      value: <Text>{formatNumber(PRICE, false)}</Text>,
    },
    {
      label: <Text>Proceed Amount</Text>,
      value: <Text>Rp {formatNumber(SELL_AMOUNT, false)}</Text>,
    },
    {
      label: <Text>Fee</Text>,
      value: <Text>Rp {formatNumber(SELL_FEE, false)}</Text>,
    },
    {
      label: <Text family="bold">Total Proceeds</Text>,
      value: (
        <Text family="bold">
          Rp{' '}
          {formatNumber(
            getSellAmountInvestedFee(LOT, price, isCorporateAction),
            false,
          )}
        </Text>
      ),
    },
    {
      label: <Text>Profit/Loss</Text>,
      value: (
        <Text color={getColorBasedOnNumber(sellProfitLoss)}>
          {formatNumber(sellProfitLoss, false)} ({sellProfitLoss > 0 && '+'}
          {formatPercentage(sellGain, false)})
        </Text>
      ),
    },
  ];

  const loadCompanyData = useCallback(async () => {
    try {
      setCompanyLoading(true);
      await getCompany(symbol);
      setCompanyLoading(false);
    } catch (e) {
      onCancel();
    }
  }, [symbol]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  return (
    <Flex direction="column" width="100%">
      <SkeletonWrapper
        skeleton={
          <Skeleton
            title={false}
            paragraph={{ rows: 6, style: { margin: '20px' } }}
            active
          />
        }
        loading={isCompanyLoading}
      >
        {previewData.map((p) => (
          <PreviewItem key={`sell-all-${p.label}`}>
            {p.label}
            {p.value}
          </PreviewItem>
        ))}
      </SkeletonWrapper>
    </Flex>
  );
}

export default SellAllPreview;
